const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "DIV",
  "SPAN",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "S",
  "STRIKE",
  "UL",
  "OL",
  "LI",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "A",
  "TABLE",
  "THEAD",
  "TBODY",
  "TFOOT",
  "TR",
  "TD",
  "TH",
  "HR",
  "PRE",
  "CODE",
  "SUB",
  "SUP",
  "FONT",
]);

const REMOVE_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "IMG",
  "VIDEO",
  "AUDIO",
  "FORM",
  "INPUT",
  "BUTTON",
  "TEXTAREA",
  "SELECT",
  "LINK",
  "META",
  "BASE",
  "SVG",
  "CANVAS",
  "NOSCRIPT",
]);

const ALLOWED_STYLES = new Set([
  "color",
  "background-color",
  "font-weight",
  "font-style",
  "font-size",
  "text-decoration",
  "text-align",
  "margin-left",
  "padding-left",
  "list-style-type",
]);

const ALLOWED_ATTRS = {
  A: new Set(["href", "title", "target", "rel"]),
  TD: new Set(["colspan", "rowspan", "align"]),
  TH: new Set(["colspan", "rowspan", "align"]),
  FONT: new Set(["color", "size"]),
  OL: new Set(["start", "type"]),
  LI: new Set(["value"]),
};

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function looksLikeHtml(value) {
  if (!value || typeof value !== "string") return false;
  return /<\/?[a-z][a-z0-9]*\b/i.test(value);
}

const PLAIN_BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "TR",
  "BLOCKQUOTE",
  "PRE",
  "TABLE",
  "HR",
]);

function collectPlainText(node, parts) {
  if (node.nodeType === 3) {
    parts.push(node.nodeValue || "");
    return;
  }
  if (node.nodeType !== 1) return;
  const tag = node.tagName;
  if (tag === "STYLE" || tag === "SCRIPT" || tag === "NOSCRIPT") return;
  if (tag === "BR" || tag === "HR") {
    parts.push("\n");
    return;
  }
  const isBlock = PLAIN_BLOCK_TAGS.has(tag);
  if (isBlock) parts.push("\n");
  Array.from(node.childNodes).forEach((child) =>
    collectPlainText(child, parts),
  );
  if (isBlock) parts.push("\n");
}

export function htmlToPlainText(html) {
  if (!html) return "";
  if (typeof document === "undefined") {
    return String(html)
      .replace(/<(br|hr)\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|h[1-6]|li|tr|blockquote|pre)>/gi, "\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  const div = document.createElement("div");
  div.innerHTML = looksLikeHtml(html) ? html : escapeHtml(html);
  const parts = [];
  collectPlainText(div, parts);
  return parts
    .join("")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function isEmptyNovedadHtml(html) {
  return !htmlToPlainText(html);
}

export function plainTextToHtml(text) {
  if (!text) return "";
  const escaped = escapeHtml(text).replace(
    /\*\*([^*]+)\*\*/g,
    "<strong>$1</strong>",
  );
  const paragraphs = escaped.split(/\n{2,}/);
  return paragraphs.map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
}

function extractHtmlFragment(html) {
  const start = html.indexOf("<!--StartFragment-->");
  const end = html.indexOf("<!--EndFragment-->");
  if (start !== -1 && end !== -1 && end > start) {
    return html.slice(start + "<!--StartFragment-->".length, end);
  }
  const body = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (body) return body[1];
  return html;
}

function sanitizeStyle(value) {
  if (!value) return "";
  const kept = [];
  String(value)
    .split(";")
    .forEach((part) => {
      const idx = part.indexOf(":");
      if (idx < 0) return;
      const prop = part.slice(0, idx).trim().toLowerCase();
      const val = part.slice(idx + 1).trim();
      if (!ALLOWED_STYLES.has(prop)) return;
      if (/expression|javascript:|url\s*\(/i.test(val)) return;
      kept.push(`${prop}: ${val}`);
    });
  return kept.join("; ");
}

function sanitizeHref(value) {
  const href = String(value || "").trim();
  if (!href) return "";
  if (/^(https?:|mailto:|#)/i.test(href)) return href;
  return "";
}

function unwrap(node) {
  const parent = node.parentNode;
  if (!parent) {
    node.remove();
    return;
  }
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

function cleanNode(node) {
  if (node.nodeType === 8) {
    node.remove();
    return;
  }
  if (node.nodeType !== 1) return;

  const tag = node.tagName;
  if (REMOVE_TAGS.has(tag) || tag.includes(":")) {
    node.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tag) && tag !== "BODY" && tag !== "HTML") {
    const children = Array.from(node.childNodes);
    children.forEach(cleanNode);
    unwrap(node);
    return;
  }

  const allowed = ALLOWED_ATTRS[tag];
  Array.from(node.attributes || []).forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on") || name === "class" || name === "id") {
      node.removeAttribute(attr.name);
      return;
    }
    if (name === "style") {
      const next = sanitizeStyle(attr.value);
      if (next) node.setAttribute("style", next);
      else node.removeAttribute("style");
      return;
    }
    const attrAllowed =
      allowed && Array.from(allowed).some((a) => a.toLowerCase() === name);
    if (!attrAllowed) {
      node.removeAttribute(attr.name);
      return;
    }
    if (name === "href") {
      const href = sanitizeHref(attr.value);
      if (href) {
        node.setAttribute("href", href);
        if (node.getAttribute("target") === "_blank") {
          node.setAttribute("rel", "noopener noreferrer");
        }
      } else {
        node.removeAttribute("href");
      }
    }
  });

  Array.from(node.childNodes).forEach(cleanNode);
}

export function sanitizeNovedadHtml(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  if (typeof DOMParser === "undefined") {
    return looksLikeHtml(dirty) ? dirty : plainTextToHtml(dirty);
  }

  const fragment = extractHtmlFragment(dirty)
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<\/?o:p[^>]*>/gi, "");

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="novedad-html-root">${fragment}</div>`,
    "text/html",
  );
  const root = doc.getElementById("novedad-html-root") || doc.body;
  Array.from(root.childNodes).forEach(cleanNode);
  return root.innerHTML.trim();
}

function decodeEscapedHtmlIfNeeded(value) {
  if (!value || looksLikeHtml(value)) return value;
  if (!/&lt;\/?[a-z]/i.test(value)) return value;
  const decoded =
    typeof document !== "undefined"
      ? (() => {
          const ta = document.createElement("textarea");
          ta.innerHTML = value;
          return ta.value;
        })()
      : String(value)
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, "&");
  return looksLikeHtml(decoded) ? decoded : value;
}

export function novedadHtmlForDisplay(value) {
  if (!value) return "";
  const html = decodeEscapedHtmlIfNeeded(value);
  return looksLikeHtml(html)
    ? sanitizeNovedadHtml(html)
    : sanitizeNovedadHtml(plainTextToHtml(html));
}

export const novedadHtmlBodySx = {
  fontSize: "0.95rem",
  lineHeight: 1.7,
  color: "text.primary",
  wordBreak: "break-word",
  "& p": { m: 0, mb: "0.85em" },
  "& > *:last-child": { mb: 0 },
  "& div": { display: "block" },
  "& div + div": { mt: "0.55em" },
  "& ul, & ol": { m: "0 0 12px", pl: 2.75 },
  "& li": { mb: 0.45 },
  "& li > p": { mb: 0.35 },
  "& strong, & b": { fontWeight: 700 },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    fontSize: "1.02rem",
    fontWeight: 800,
    mt: 1.25,
    mb: 0.65,
    lineHeight: 1.35,
  },
  "& font": { display: "inline" },
  "& a": { color: "primary.main" },
  "& blockquote": {
    m: "8px 0",
    pl: 1.5,
    borderLeft: "3px solid",
    borderColor: "divider",
    color: "text.secondary",
  },
  "& table": { borderCollapse: "collapse", width: "100%", mb: 1.5 },
  "& td, & th": {
    border: "1px solid",
    borderColor: "divider",
    p: "4px 8px",
  },
};
